/** @jsxImportSource @emotion/react */
import Toast from 'components/atoms/Toast'
import React, { useState } from 'react'
import { Button, Form, Modal } from 'react-bootstrap'
import {
  useGetNeedsWorkPostCheckQuery,
  useResolveNeedsWorkMutation,
  useResolveNeedsWorkRemitMutation,
} from 'store/services/needsWork'
import { NeedsWorkItemDto, NeedsWorkRemitAction } from 'store/types'

// The server's refusal to post as received, and the reasons after it.
const NOT_POSTABLE = 'cannot be posted as received: '

type Props = {
  item: NeedsWorkItemDto
  onClose: () => void
  onDone?: () => void
  // "Post with corrections" continues in the posting form; the item resolves
  // when that form posts.
  onPostWithCorrections: (note: string) => void
}

const REMIT_ACTIONS: { value: NeedsWorkRemitAction; label: string; description: string }[] = [
  {
    value: 'POST',
    label: 'Post as received',
    description: 'The payer confirmed it processed correctly — post the remit as it came.',
  },
  {
    value: 'POST_WITH_CORRECTIONS',
    label: 'Post with corrections',
    description: 'Open the posting form filled from the remit, correct it, and post.',
  },
  {
    value: 'DISMISS',
    label: 'Dismiss',
    description: 'Archive the remit without posting — e.g. the payer is sending a corrected one.',
  },
]

export const NeedsWorkResolveModal: React.FC<Props> = ({ item, onClose, onDone, onPostWithCorrections }) => {
  const parkedRemit = item.remit?.parked ? item.remit : null
  const [note, setNote] = useState('')
  const [action, setAction] = useState<NeedsWorkRemitAction | ''>('')
  const [submitted, setSubmitted] = useState(false)
  const [resolve, { isLoading: resolving }] = useResolveNeedsWorkMutation()
  const [resolveRemit, { isLoading: settling }] = useResolveNeedsWorkRemitMutation()
  const busy = resolving || settling
  const actionMissing = Boolean(parkedRemit) && !action

  // Asked of the server, which checks the remit against its charge the way the
  // posting form would before posting it: when it does not line up, the
  // posting form is where the biller settles it.
  const { data: postCheck } = useGetNeedsWorkPostCheckQuery(item.id, {
    skip: !parkedRemit,
    refetchOnMountOrArgChange: true,
  })
  const missing = postCheck?.postable === false ? postCheck.reasons : []
  const missingText = missing.join('; ')

  const continueInPostingForm = (reason?: string) => {
    if (reason) {
      Toast({ type: 'info', label: `Can't post as received — ${reason}. Finish it in the posting form.` })
    }

    onPostWithCorrections(note.trim())
    onClose()
  }

  const submit = async () => {
    setSubmitted(true)

    if (actionMissing) {
      return
    }

    if (parkedRemit && action === 'POST_WITH_CORRECTIONS') {
      continueInPostingForm()
      return
    }

    if (parkedRemit && action === 'POST' && missing.length) {
      continueInPostingForm(missingText)
      return
    }

    try {
      if (parkedRemit && action) {
        await resolveRemit({
          id: item.id,
          claimId: parkedRemit.claimId,
          note: note.trim() || null,
          remitAction: action,
        }).unwrap()
        Toast({
          type: 'success',
          label: action === 'DISMISS' ? 'Remit dismissed — item resolved' : 'Remit posted — item resolved',
        })
      } else {
        await resolve({ id: item.id, note: note.trim() || undefined }).unwrap()
        Toast({ type: 'success', label: 'Needs Work item resolved' })
      }

      onDone?.()
      onClose()
    } catch (error) {
      const message = (error as { data?: { message?: string } })?.data?.message

      if (parkedRemit && action === 'POST' && message?.includes(NOT_POSTABLE)) {
        continueInPostingForm(message.slice(message.indexOf(NOT_POSTABLE) + NOT_POSTABLE.length))
        return
      }

      Toast({ type: 'error', label: message || 'Could not resolve the item' })
    }
  }

  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header>
        <Modal.Title css={{ fontSize: '1.1rem' }}>Resolve Needs Work — {item.reasonLabel}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {parkedRemit && (
          <div className="mb-3">
            <div css={{ fontSize: '0.813rem', fontWeight: 600, marginBottom: 6 }}>
              What to do with the parked remit
              {parkedRemit.claimNumber ? ` (${parkedRemit.claimNumber})` : ''}
            </div>
            {REMIT_ACTIONS.map((option) => (
              <Form.Check
                key={option.value}
                id={`remit-action-${option.value}`}
                type="radio"
                name="remit-action"
                className="mb-2"
                checked={action === option.value}
                disabled={busy}
                onChange={() => setAction(option.value)}
                label={(
                  <span>
                    <span css={{ fontWeight: 600 }}>{option.label}</span>
                    <span className="text-muted" css={{ display: 'block', fontSize: '0.75rem' }}>
                      {option.description}
                    </span>
                    {option.value === 'POST' && missing.length > 0 && (
                      <span css={{ display: 'block', fontSize: '0.75rem', color: '#b35c00' }}>
                        Can't post as it came — {missingText}. This opens the posting form to settle it.
                      </span>
                    )}
                  </span>
                )}
              />
            ))}
            {submitted && actionMissing && (
              <div css={{ color: '#dc3545', fontSize: '0.75rem' }}>Choose what to do with the remit</div>
            )}
          </div>
        )}
        <div className="form-group mb-0">
          <label css={{ fontSize: '0.813rem', fontWeight: 600 }}>Closing note (optional)</label>
          <textarea
            className="form-control"
            rows={3}
            maxLength={1000}
            value={note}
            disabled={busy}
            placeholder="What was done"
            onChange={(event) => setNote(event.target.value)}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" size="sm" disabled={busy} onClick={onClose}>
          Cancel
        </Button>
        <Button variant="success" size="sm" disabled={busy} onClick={submit}>
          {busy
            ? 'Resolving…'
            : action === 'POST_WITH_CORRECTIONS' || (action === 'POST' && missing.length)
              ? 'Continue to posting'
              : 'Resolve'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
